<template>
  <slot></slot>
  <modal :id="modalId" :title="title">
    <form :id="formId" :action="action" method="POST">

      <div class="form-group row">
        <div class="offset-sm-3 col-sm-9">
          <p v-if="mode === 'multi'">You can assign, reassign or unassign
            <strong v-text="count"></strong> selected claim(s).
          </p>
          <p v-else>You can assign, reassign or unassign this claim.</p>
          <input v-for="claimId in claimIds" type="hidden" name="claim_assign_selected_claim[]" :value="claimId">
        </div>
      </div>

      <div class="form-group row" :class="{'has-error': hasError('team')}">
        <label for="assign_team" class="col-form-label col-sm-3">Team:</label>
        <div class="col-sm-9">
          <chosen id="assign_team" name="assign_team"
                  :options="data.teams"
                  :chosen-options="{allow_single_deselect: true}"
                  track-by="id"
                  label="name"
                  placeholder="Select a team..."
                  class="form-control form-control-sm required"
                  v-model="data.assignTeam"></chosen>
        </div>
      </div>

      <div class="form-group row" :class="{'has-error': hasError('teamlead')}">
        <label for="assign_team_lead" class="col-form-label col-sm-3">Account Manager:</label>
        <div class="col-sm-9">
          <chosen id="assign_team_lead" name="assign_team_lead"
                  :options="data.teamLeads"
                  :chosen-options="{allow_single_deselect: true}"
                  track-by="id"
                  label="name"
                  placeholder="Select an account manager..."
                  class="form-control form-control-sm required"
                  :disabled="!enabled.teamlead"
                  v-model="data.assignTeamLead"></chosen>
        </div>
      </div>

      <div class="form-group row" :class="{'has-error': hasError('adjuster')}">
        <label for="assign_adjuster" class="col-form-label col-sm-3">Adjuster:</label>
        <div class="col-sm-9">
          <chosen id="assign_adjuster" name="assign_adjuster"
                  :options="data.adjusters"
                  :chosen-options="{allow_single_deselect: true}"
                  track-by="id"
                  label="name"
                  placeholder="Select an adjuster..."
                  class="form-control form-control-sm required"
                  :disabled="!enabled.adjuster"
                  v-model="data.assignAdjuster"></chosen>
        </div>
      </div>

      <div class="form-group row" :class="{'has-error': hasError('examiner')}">
        <label for="assign_examiner" class="col-form-label col-sm-3">Examiner:</label>
        <div class="col-sm-9">
          <chosen id="assign_examiner" name="assign_examiner"
                  :options="data.examiners"
                  :chosen-options="{allow_single_deselect: true}"
                  track-by="id"
                  label="name"
                  placeholder="Select an examiner..."
                  class="form-control form-control-sm required"
                  :disabled="!enabled.examiner"
                  v-model="data.assignExaminer"></chosen>
        </div>
      </div>

      <input type="hidden" name="_token" :value="$csrf_token">
    </form>

    <template #footer>
      <button type="button" class="btn btn-default" @click="hideModal">
        <i class="fa fa-fw fa-close"></i> Cancel
      </button>
      <button type="submit" class="btn btn-primary" :disabled="!submitEnable" :form="formId">
        <i class="fa fa-fw fa-save"></i> Save changes
      </button>
    </template>

  </modal>
</template>

<script>
import HasModal from "./../mixins/HasModal";
import { mapState } from 'pinia'
import { useCheckboxListStore } from "../../stores/checkboxList"

export default {

  /**
   *
   * Modal form allows to set team, account manager, adjuster and examiner to claim.
   *
   * Input properties:
   *
   * modalId - custom id for modal component.
   * action  - form action URL.
   * title   - form title.
   * mode    - 'single' or 'multi'; in case of 'milti', selected claims ids should be passed via event.
   * claim   - serialized claim data.
   * teams   - teams array.
   *
   * Usage examples:
   *
   * <assign-claim-modal modal-id="assignClaimsModal"
   *                     action="{{ route('claims.assign') }}"
   *                     :teams="{{ json_encode($assign['teams']) }}"
   *                     :claim="{{ json_encode($claim) }}"
   *                     mode="single"
   *                     title="Claim Assignment">
   *   <a class="btn btn-sm btn-success" href="#" v-modal="'assignClaimsModal'">
   *     <i class="fa fa-fw fa-users"></i> Assign
   *   </a>
   * </assign-claim-modal>
   *
   */

  mixins: [HasModal],

  props: {

    // Modal dialog id.
    modalId: {
      required: false,
      type: String,
      default: 'assignClaimsModal'
    },

    // Form action URL.
    action: {
      type: String,
      default: ""
    },

    // Form title.
    title: {
      type: String,
      default: ""
    },

    //
    mode: {
      required: false,
      type: String,
      default: "single",
    },

    //
    claim: {
      required: false,
      type: Object,
      default: () => {
        return {};
      }
    },

    teams: {
      required: false,
      type: Array,
      default: () => {
        return [];
      }
    }

  },

  watch: {
    "data.assignTeam": function (val) {
      this.teamChanged();
    },
  },

  /**
   * Component initialization.
   */
  mounted: function () {
    this.teamChanged();
  },

  /**
   * Variables (models).
   */
  data() {
    return {
      data: {
        teams: this.teams,
        assignTeam: this.claim.team_id || '',
        assignTeamLead: '',
        assignAdjuster: '',
        assignExaminer: '',
        teamLeads: [],
        adjusters: [],
        examiners: [],
      },
      enabled: {
        'teamlead': true,
        'adjuster': true,
        'examiner': true,
      }
    }
  },

  /**
   * Computed values
   */
  computed: {
    ...mapState(useCheckboxListStore, ['checkedIds', 'checkedCount']),

    count() {
      return this.checkedCount();
    },

    formId() {
      return this.modalId + "-form"
    },

    fieldsHasData() {

      return this.data.assignTeam !== undefined && this.data.assignTeam !== '' &&
          this.data.assignTeamLead !== undefined && this.data.assignTeamLead !== '' &&
          this.data.assignAdjuster !== undefined && this.data.assignAdjuster !== '' &&
          this.data.assignExaminer !== undefined && this.data.assignExaminer !== '';
    },

    submitEnable() {

      let enabled = false;

      // If something selected in every dropbox.
      if (this.fieldsHasData) {

        // Enabled if Team and TeamLead are chosen or Unassign in every dropbox.
        enabled = (!!parseInt(this.data.assignTeam) && !!parseInt(this.data.assignTeamLead)) ||
            (!parseInt(this.data.assignTeam) && !parseInt(this.data.assignTeamLead) && !parseInt(this.data.assignAdjuster) && !parseInt(this.data.assignExaminer));
      }

      return enabled;
    },

    selectedTeam() {

      let teamId = this.data.assignTeam;
      return this.data.teams.find(item => item['id'] === parseInt(teamId));
    },

    defaultTeamId() {

      if (this.selectedTeam)
        return parseInt(this.selectedTeam['team-lead-id']) || 0;
      else
        return 0;
    },

    isTeamChanged() {
      return !this.claim || parseInt(this.claim.team_id) !== parseInt(this.data.assignTeam);
    },

    claimIds() {
      return this.mode === 'single' ? [this.claim.id] : this.checkedIds();
    },

  },

  /**
   * Functions (methods).
   */
  methods: {

    initializeFormFields(data) {

      if (typeof data === 'undefined') data = {};

      this.data = Object.assign(this.data, this.emptyData, data);

      this.data.assignTeam = '';
      if(this.claim.team_id !== undefined) {
        this.data.assignTeam = this.claim.team_id;
      }
    },

    // Specific functions.

    hasError(field) {

      switch (field) {
        case 'team':
          return this.data.assignTeam === undefined || this.data.assignTeam === '';
        case 'teamlead':
          return this.data.assignTeamLead === undefined || this.data.assignTeamLead === '';
        case 'adjuster':
          return this.data.assignAdjuster === undefined || this.data.assignAdjuster === '';
        case 'examiner':
          return this.data.assignExaminer === undefined || this.data.assignExaminer === '';
      }

      return false;
    },

    setExistingTeamLeadAdjusterExaminer() {

      let defaultTeamLeadFound = this.data.teamLeads.find(item => item.id === this.defaultTeamId);

      this.data.assignTeamLead = defaultTeamLeadFound ? this.defaultTeamId : '';
      this.data.assignAdjuster = 0;
      this.data.assignExaminer = 0;

      if (!this.isTeamChanged) {
        this.data.assignTeamLead = this.claim.team_lead_id ?? 0;
        this.data.assignAdjuster = this.claim.adjuster_id ?? 0;
        this.data.assignExaminer = this.claim.examiner_id ?? 0;
      }
    },

    teamChanged() {

      let _this = this;
      let team_id = this.data.assignTeam;

      Promise.all([this.updateTeamLead(team_id), this.updateAdjuster(team_id), this.updateExaminer(team_id)]).then(values => {

        _this.setExistingTeamLeadAdjusterExaminer();
      });

    },

    updateTeamLead(team_id) {
      return this.update(team_id, 'teamlead', this.data.teamLeads, (val) => {
        this.data.assignTeamLead = val;
      });
    },

    updateAdjuster(team_id) {
      return this.update(team_id, 'adjuster', this.data.adjusters, (val) => {
        this.data.assignAdjuster = val;
      });
    },

    updateExaminer(team_id) {
      return this.update(team_id, 'examiner', this.data.examiners, (val) => {
        this.data.assignExaminer = val;
      });
    },

    update(team_id, type, $list, $setVal) {

      let _this = this;

      function loadData(data) {

        $list.splice(0, $list.length);

        $list.push({id: '', name: ''});
        $list.push({id: 0, name: 'Unassign/Do not assign'});
        $setVal(0);

        _this.enabled[type] = true;

        data.forEach(function (item) {
          $list.push(item);
        });
      }

      function loading() {

        $list.splice(0, $list.length);
        $list.push({id: '', name: 'loading...'});
        $setVal('');

        _this.enabled[type] = false;
      }

      return new Promise(function (resolve, reject) {

        if (team_id && team_id === '') { // nothing is selected

          $list.splice(0, $list.length);
          $setVal('');
          _this.enabled[type] = false;

          resolve();
        } else if (!!parseInt(team_id)) { // a team is selected
          loading();

          axios.get('/webapi/team/' + team_id + '/' + type + 's')
              .then(function (response) {

                loadData(response.data);

                resolve();
              })
              .catch(function (error) {

                $list.splice(0, $list.length);
                $list.push({id: '', name: 'Loading failed'});
                $setVal('');

                _this.enabled[type] = false;

                resolve();
              });
        } else { // unassign is selected

          loadData([]);

          resolve();
        }
      });
    },

  }
}
</script>
